import type { SellerJourney, SellerProjectFunnel, SellerProjectPhase } from "@recruiter/shared";

import { plural } from "../../lib/format";

// "Where this project stands": the whole project's funnel as COUNTS of talents (the backend
// never sends who, or what anyone quoted), with THIS talent's own steps marked "You". Bars
// are relative to how many talents Mira contacted, so a step reads as a share of the field.
// It renders in the open project's panel, beside the conversation, under the panel's own
// title and `funnelHeadline`.

type StepKey = keyof SellerProjectFunnel;

const STEPS: ReadonlyArray<{
  key: StepKey;
  label: string;
  hint: string;
  reached: (you: SellerJourney) => boolean;
}> = [
  {
    key: "contacted",
    label: "Contacted by Mira",
    hint: "Talents Mira reached out to about this project",
    reached: () => true,
  },
  {
    key: "replied",
    label: "Replied",
    hint: "Wrote back to Mira",
    reached: (you) => you.replied,
  },
  {
    key: "proposals",
    label: "Sent a proposal",
    hint: "Proposals the client can see",
    reached: (you) => you.proposed,
  },
  {
    key: "picked",
    label: "Picked by the client",
    hint: "Talents the client chose",
    reached: (you) => you.picked,
  },
  {
    key: "direct",
    label: "Talking with the client",
    hint: "The client took the conversation over",
    reached: (you) => you.direct,
  },
];

/** The whole funnel in one sentence: the section's summary line. */
export function funnelHeadline(f: SellerProjectFunnel, phase: SellerProjectPhase): string {
  if (phase === "closed") {
    return f.picked > 0
      ? `Wrapped up. The client picked ${plural(f.picked, "talent")}.`
      : "Wrapped up without a pick.";
  }
  if (phase === "choosing") {
    return `The client is choosing between ${plural(f.proposals, "proposal")}.`;
  }
  return `Mira has contacted ${plural(f.contacted, "talent")} so far and ${
    f.proposals === 0
      ? "no proposals are in yet"
      : `${plural(f.proposals, "proposal")} ${f.proposals === 1 ? "is" : "are"} in`
  }.`;
}

export function ProjectFunnel({
  funnel,
  you,
  settled,
}: {
  funnel: SellerProjectFunnel;
  you: SellerJourney;
  /** This talent's part is over (declined, or the project closed without them): their
   *  steps stay marked, but nothing reads as where they stand now. */
  settled: boolean;
}) {
  const base = Math.max(funnel.contacted, 1);
  // The deepest step this talent reached is where they stand now.
  const deepest = STEPS.reduce((at, step, i) => (step.reached(you) ? i : at), 0);
  return (
    <ol className="sl-funnel__steps" aria-label="Where this project stands">
      {STEPS.map((step, i) => {
        const count = funnel[step.key];
        const share = count === 0 ? 0 : Math.max(count / base, 0.04);
        const reached = step.reached(you);
        const here = !settled && i === deepest;
        return (
          <li
            key={step.key}
            className="sl-funnel__step"
            data-reached={reached}
            data-here={here}
            style={{ animationDelay: `${120 + i * 70}ms` }}
          >
            <div className="sl-funnel__label">
              <span className="sl-funnel__name">{step.label}</span>
              <span className="sl-funnel__hint">{step.hint}</span>
            </div>
            <div className="sl-funnel__track" aria-hidden="true">
              <span
                className="sl-funnel__fill"
                style={{
                  width: `${share * 100}%`,
                  animationDelay: `${200 + i * 90}ms`,
                }}
              />
            </div>
            <span className="sl-funnel__count">{count}</span>
            <span className="sl-funnel__you">
              {reached ? (
                <span className={`sl-you${here ? " sl-you--here" : ""}`}>
                  {here ? "You are here" : "You"}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
